import { Node } from '@antv/x6';
import { DeviceType, HALFrameType, HALEthFrame, HALInterface, IHALDevice, IHALInterface, ISimulatorDevice } from './Base';
import { ProtocolType } from './Ethernet';
import { generateRandomMac } from '../../../tools';

// 防火墙规则方向
export enum FirewallDirection {
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND',
}

// 规则动作
export enum FirewallAction {
  Allow = 'ALLOW',
  Deny = 'DENY',
}

export type FirewallRule = {
  id: string;
  direction: FirewallDirection;
  protocol: ProtocolType;
  port?: number;
  action: FirewallAction;
};

// 默认简单模式规则：关闭ICMP，开放HTTP/HTTPS，仅允许内到外
export const DEFAULT_SIMPLE_RULES: FirewallRule[] = [
  { id: 'simple-in-icmp', direction: FirewallDirection.Inbound, protocol: ProtocolType.ICMP, action: FirewallAction.Deny },
  { id: 'simple-out-icmp', direction: FirewallDirection.Outbound, protocol: ProtocolType.ICMP, action: FirewallAction.Allow },
  { id: 'simple-out-http', direction: FirewallDirection.Outbound, protocol: ProtocolType.HTTP, action: FirewallAction.Allow },
  { id: 'simple-out-https', direction: FirewallDirection.Outbound, protocol: ProtocolType.HTTPS, action: FirewallAction.Allow },
];

// 防火墙接口，单纯二层转发，不持有IP
class FirewallInterface extends HALInterface {
  HAL_Recv(frame: HALEthFrame, callback: Function): void {
    const { fromMAC, toMAC, type, data } = frame;
    if (fromMAC === this.mac) {
      return;
    }
    this.device.HAL_Recv(this, fromMAC, toMAC, type, data, callback);
  }
}

// 防火墙设备（简化：仅双口透传+规则检查）
export class FirewallDevice implements ISimulatorDevice, IHALDevice {
  private node: Node;
  private bridge: any;
  private inner: FirewallInterface;
  private outer: FirewallInterface;
  private enabled: boolean;
  private simpleMode: boolean;
  private rules: FirewallRule[];

  constructor(node: Node) {
    this.node = node;
    this.inner = new FirewallInterface(this, generateRandomMac());
    this.outer = new FirewallInterface(this, generateRandomMac());
    this.enabled = true;
    this.simpleMode = true;
    this.rules = [...DEFAULT_SIMPLE_RULES];
  }

  get deviceType() {
    return DeviceType.Firewall;
  }

  getNode(): Node {
    return this.node;
  }

  setBridge(bridge: any): void {
    this.bridge = bridge;
  }

  getPorts(): Array<IHALInterface> {
    return [this.inner, this.outer];
  }

  destory(): void {
    this.inner.unlink();
    this.outer.unlink();
  }

  // 规则设置
  setEnabled(enabled: boolean) {
    this.enabled = !!enabled;
    this.notifyChange();
  }

  getConfig() {
    return {
      enabled: this.enabled,
      simpleMode: this.simpleMode,
      rules: this.rules,
    };
  }

  setSimpleMode(simple: boolean) {
    this.simpleMode = !!simple;
    if (this.simpleMode) {
      this.rules = [...DEFAULT_SIMPLE_RULES];
    }
    this.notifyChange();
  }

  updateRules(rules: FirewallRule[]) {
    if (!Array.isArray(rules)) {
      return;
    }
    this.rules = rules
      .filter((r) => r && r.id && r.direction && r.action && typeof r.protocol === 'number')
      .map((r) => ({ ...r }));
    this.notifyChange();
  }

  private notifyChange() {
    if (this.bridge && this.bridge.onGraphChange) {
      this.bridge.onGraphChange();
    }
  }

  // 简化的报文方向判断：从inner到outer视为Outbound，反向视为Inbound
  private judgeDirection(from: IHALInterface): FirewallDirection {
    return from === this.inner ? FirewallDirection.Outbound : FirewallDirection.Inbound;
  }

  private matchRule(direction: FirewallDirection, protocol: ProtocolType): FirewallAction | null {
    const rules = this.simpleMode ? DEFAULT_SIMPLE_RULES : this.rules;
    const hit = rules.find((r) => r.direction === direction && r.protocol === protocol);
    return hit ? hit.action : null;
  }

  // 二层透传 + 协议判断（基于以太网帧类型简化映射）
  HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
    const target = fromInterface === this.inner ? this.outer : this.inner;

    if (!this.enabled) {
      target.HAL_Send_Transparent(fromMAC, toMAC, type, data, callback);
      return;
    }

    const direction = this.judgeDirection(fromInterface);
    let protocol: ProtocolType | null = null;

    if (type === HALFrameType.IPv4 && data && typeof data.protocol === 'number') {
      protocol = data.protocol as ProtocolType;
    }

    if (type === HALFrameType.IPv4 && protocol !== null) {
      const action = this.matchRule(direction, protocol);
      if (action === FirewallAction.Deny) {
        return; // 丢弃
      }
    }

    // 默认透传
    target.HAL_Send_Transparent(fromMAC, toMAC, type, data, callback);
  }

  toJSON() {
    return {
      deviceType: DeviceType.Firewall,
      enabled: this.enabled,
      simpleMode: this.simpleMode,
      rules: this.rules,
      ports: [this.inner.toJSON(), this.outer.toJSON()],
    };
  }

  fromJSON(config: any): void {
    if (!config) return;
    this.enabled = config.enabled !== undefined ? !!config.enabled : this.enabled;
    this.simpleMode = config.simpleMode !== undefined ? !!config.simpleMode : this.simpleMode;
    if (Array.isArray(config.rules)) {
      this.rules = config.rules;
    }
    const { ports } = config;
    if (Array.isArray(ports) && ports.length === 2) {
      this.inner.fromJSON(ports[0]);
      this.outer.fromJSON(ports[1]);
    }
  }
}

// 默认配置导出，便于UI复用
export const DEFAULT_FIREWALL_STATE = {
  enabled: true,
  simpleMode: true,
  rules: DEFAULT_SIMPLE_RULES,
};
